import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { ReportTemplateConfigFormData } from 'dashboard/forms/FormReportTemplate';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearReportTemplate } from 'dashboard/redux/actions/report-template';
import { clearReportTemplateVersion } from 'dashboard/redux/actions/report-template-version';
import { RootState } from 'dashboard/redux/store';
import {
  loadReportTemplate,
  updateReportTemplate,
} from 'dashboard/redux/thunk-actions/report-template';
import { loadReportTemplateVersion } from 'dashboard/redux/thunk-actions/report-template-version';
import { loadUserList } from 'dashboard/redux/thunk-actions/user';
import DashboardReportTemplatesShowView from 'dashboard/views/DashboardReportTemplatesShowView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type Params = { reportTemplateId: string };

const DashboardReportTemplatesShowRoute = (props: PropsFromRedux) => {
  useOnMountAndUnmount(() => {
    props.clearReportTemplate();
    props.clearReportTemplateVersion();
  });

  const { reportTemplateId } = useParams<Params>() as Params;

  const { loading: loadingReportTemplate } = useApiLoader({
    loader: async () => {
      await props.loadReportTemplate(reportTemplateId);
    },
    deps: [reportTemplateId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load template.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: handleSaveReportTemplate, error: saveReportTemplateError } = useApiCallback(
    async (values: ReportTemplateConfigFormData) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await props.updateReportTemplate(props.reportTemplate.type, reportTemplateId, values);
      props.showMessage(
        'Your changes were successfully saved, and will affect all future report runs',
        MessageType.Success,
      );
    },
    [props.updateReportTemplate, reportTemplateId, props.reportTemplate],
  );

  useApiErrorHandler({
    error: saveReportTemplateError,
    onNetworkError: () =>
      props.showMessage('Failed to save template. Please try again.', MessageType.Failure),
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to save template. Please try again.', MessageType.Failure);
      }
    },
  });

  const canEdit = usePermission(Permission.ReportTemplateWrite);

  return (
    <DashboardReportTemplatesShowView
      isLoading={loadingReportTemplate}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      reportTemplate={props.reportTemplate}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      latestPublishedVersion={props.latestPublishedVersion}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      reportTemplateVersion={props.reportTemplateVersion}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      reportTemplateMeta={props.reportTemplateMeta}
      canEdit={canEdit}
      handleSaveReportTemplate={handleSaveReportTemplate}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      refreshReportTemplate={() => props.loadReportTemplate(props.reportTemplate.id)}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    reportTemplate: state.reportTemplate.reportTemplate,
    latestPublishedVersion: state.reportTemplate.latestPublishedVersion,
    reportTemplateVersion: state.reportTemplateVersion.reportTemplateVersion,
    reportTemplateMeta: state.reportTemplate.meta,
    organization: state.organization.organization,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    featureFlags: state.featureFlags.featureFlags,
  };
};

const mapDispatchToProps = {
  clearReportTemplate,
  clearReportTemplateVersion,
  loadReportTemplate,
  loadReportTemplateVersion,
  loadUserList,
  showMessage,
  updateReportTemplate,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardReportTemplatesShowRoute);
